import { Amplify } from 'aws-amplify'
import { Auth } from '@aws-amplify/auth'
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import type { User } from '@/types/auth'

// Configure Amplify with Cognito settings
export const configureAmplify = () => {
  const config = {
    Auth: {
      region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolWebClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
      authenticationFlowType: 'USER_SRP_AUTH',
      oauth: {
        domain: import.meta.env.VITE_COGNITO_DOMAIN,
        scope: ['email', 'openid', 'profile'],
        redirectSignIn: import.meta.env.DEV 
          ? 'http://localhost:3000/auth/callback'
          : `${window.location.origin}/auth/callback`,
        redirectSignOut: import.meta.env.DEV 
          ? 'http://localhost:3000/logout'
          : `${window.location.origin}/logout`,
        responseType: 'code',
      },
      socialProviders: ['Google'],
    },
  }

  Amplify.configure(config)
}

// Transform Cognito user to our User type
export const transformCognitoUser = async (cognitoUser: any): Promise<User> => {
  // Get attributes directly from the user object
  let attributes = cognitoUser.attributes || {}
  
  // For some OAuth flows, attributes might be in signInUserSession
  if ((!attributes.email || !attributes.sub) && cognitoUser.signInUserSession) {
    try {
      const idToken = cognitoUser.signInUserSession.idToken
      if (idToken && idToken.payload) {
        // Extract attributes from ID token payload
        attributes = {
          ...attributes,
          sub: idToken.payload.sub || attributes.sub,
          email: idToken.payload.email || attributes.email,
          given_name: idToken.payload.given_name || attributes.given_name,
          family_name: idToken.payload.family_name || attributes.family_name,
          name: idToken.payload.name || attributes.name,
        }
        console.log('🔍 Debug: Got attributes from ID token:', attributes)
      }
    } catch (error) {
      console.log('🔍 Debug: Could not extract from ID token:', error)
    }
  }
  
  // Debug logging to see what attributes we're getting
  console.log('🔍 Debug: Cognito user attributes:', attributes)
  console.log('🔍 Debug: Full cognito user object:', cognitoUser)
  
  // Handle Google OAuth attributes - Google might provide 'name' instead of separate given_name/family_name
  let given_name = attributes.given_name || attributes['custom:given_name']
  let family_name = attributes.family_name || attributes['custom:family_name']
  
  // If we don't have given_name/family_name but we have 'name', try to parse it
  if (!given_name && !family_name && attributes.name) {
    const nameParts = attributes.name.split(' ')
    given_name = nameParts[0] || ''
    family_name = nameParts.slice(1).join(' ') || ''
    console.log('🔍 Debug: Parsed name from full name:', { given_name, family_name })
  }
  
  // Fallback: if still no names, try to extract from email
  if (!given_name && !family_name && attributes.email) {
    const emailPrefix = attributes.email.split('@')[0]
    // Handle common email formats like firstname.lastname@email.com
    if (emailPrefix.includes('.')) {
      const parts = emailPrefix.split('.')
      given_name = parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
      family_name = parts.slice(1).map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')
    } else if (emailPrefix.includes('_')) {
      const parts = emailPrefix.split('_')
      given_name = parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
      family_name = parts.slice(1).map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')
    } else {
      // Just use the email prefix as first name
      given_name = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1)
      family_name = ''
    }
    console.log('🔍 Debug: Extracted name from email:', { given_name, family_name })
  }
  
  const user = {
    id: attributes.sub,
    email: attributes.email,
    given_name: given_name || undefined,
    family_name: family_name || undefined,
    email_verified: attributes.email_verified === 'true' || attributes.email_verified === true || attributes.email_verified,
    sub: attributes.sub,
  }
  
  console.log('🔍 Debug: Final transformed user:', user)
  return user
}

// Auth utility functions
export const authUtils = {
  // Sign in with email and password
  async signIn(email: string, password: string) {
    try {
      const user = await Auth.signIn(email, password)
      return await transformCognitoUser(user)
    } catch (error) {
      throw new Error(getAuthErrorMessage(error))
    }
  },

  // Sign in with Google
  async signInWithGoogle() {
    try {
      await Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google })
    } catch (error) {
      throw new Error(getAuthErrorMessage(error))
    }
  },

  // Sign up new user
  async signUp(email: string, password: string, given_name: string, family_name: string) {
    try {
      const result = await Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          given_name,
          family_name,
        },
      })
      return result
    } catch (error) {
      throw new Error(getAuthErrorMessage(error))
    }
  },

  // Confirm sign up with verification code
  async confirmSignUp(email: string, code: string) {
    try {
      await Auth.confirmSignUp(email, code)
    } catch (error) {
      throw new Error(getAuthErrorMessage(error))
    }
  },

  // Resend confirmation code
  async resendConfirmationCode(email: string) {
    try {
      await Auth.resendSignUp(email)
    } catch (error) {
      throw new Error(getAuthErrorMessage(error))
    }
  },

  // Sign out
  async signOut() {
    try {
      await Auth.signOut()
    } catch (error) {
      throw new Error(getAuthErrorMessage(error))
    }
  },

  // Get current authenticated user
  async getCurrentUser(): Promise<User | null> {
    try {
      const user = await Auth.currentAuthenticatedUser()
      return await transformCognitoUser(user)
    } catch (error) {
      return null
    }
  },

  // Reset password
  async resetPassword(email: string) {
    try {
      await Auth.forgotPassword(email)
    } catch (error) {
      throw new Error(getAuthErrorMessage(error))
    }
  },

  // Confirm reset password
  async confirmResetPassword(email: string, code: string, newPassword: string) {
    try {
      await Auth.forgotPasswordSubmit(email, code, newPassword)
    } catch (error) {
      throw new Error(getAuthErrorMessage(error))
    }
  },

  // Check if user session is valid
  async getSession() {
    try {
      const session = await Auth.currentSession()
      return session.isValid()
    } catch (error) {
      return false
    }
  },

  // Get access token
  async getAccessToken(): Promise<string | null> {
    try {
      const session = await Auth.currentSession()
      return session.getAccessToken().getJwtToken()
    } catch (error) {
      return null
    }
  },

  // Get ID token (preferred for API Gateway Cognito authorizers)
  async getIdToken(): Promise<string | null> {
    try {
      console.log('🎫 Auth: Attempting to get ID token...');

      const session = await Auth.currentSession();

      console.log('📋 Auth: Session status:', {
        isValid: session.isValid(),
        hasIdToken: !!session.getIdToken(),
        hasAccessToken: !!session.getAccessToken(),
        username: session.getIdToken()?.payload?.['cognito:username'] || 'unknown'
      });

      const idToken = session.getIdToken().getJwtToken();

      console.log('✅ Auth: Successfully retrieved ID token:', {
        tokenLength: idToken.length,
        tokenPreview: `${idToken.substring(0, 20)}...`,
        expiresAt: session.getIdToken().getExpiration()
      });

      return idToken;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorType = error instanceof Error ? error.name : 'Unknown';

      console.error('❌ Auth: Failed to get ID token:', {
        error: errorMessage,
        errorType
      });
      return null;
    }
  }
}

// Error message mapping
function getAuthErrorMessage(error: any): string {
  const message = error?.message || error?.toString() || 'An unknown error occurred'
  
  // Map common Cognito errors to user-friendly messages
  if (message.includes('UserNotConfirmedException')) {
    return 'Please check your email and confirm your account before signing in.'
  }
  
  if (message.includes('NotAuthorizedException')) {
    return 'Invalid email or password. Please try again.'
  }
  
  if (message.includes('UserNotFoundException')) {
    return 'No account found with this email address.'
  }
  
  if (message.includes('UsernameExistsException')) {
    return 'An account with this email already exists.'
  }
  
  if (message.includes('InvalidPasswordException')) {
    return 'Password does not meet the requirements. Please ensure it has at least 8 characters, including uppercase, lowercase, numbers, and special characters.'
  }
  
  if (message.includes('LimitExceededException')) {
    return 'Too many attempts. Please wait a moment before trying again.'
  }
  
  if (message.includes('CodeMismatchException')) {
    return 'Invalid verification code. Please check and try again.'
  }
  
  if (message.includes('ExpiredCodeException')) {
    return 'Verification code has expired. Please request a new one.'
  }
  
  // Default to original message if no mapping found
  return message
}